// @givmo/auth — the ONE email/password credential ceremony (AS-SUITE2).
//
// Every email/password door in the estate ends here. Before this existed, the same three steps —
// apply the session scope, hand the credential to Firebase, upgrade a pending second factor into a
// challenge — were written out FIVE times: `apps/board`, `apps/charity` and `apps/company` each in
// their own auth-context, plus `admin.ts` and `consumer-email.ts` in this package. OAuth had had a
// single seam (`signInWithStrategy`) from the start, and the difference showed: in AS-WEB1 the TOTP
// challenge landed in `admin.ts` and `strategy.ts` and left the other email paths dead-ending. A
// shared ceremony is what makes that class of miss impossible rather than merely unlikely, and
// `email-ceremony-rail.test.ts` is what keeps a sixth copy from being written.
//
// Each property still renders its OWN door. A governance-only or accounting-only customer signs in
// at their module's screen and it has to feel standalone; what the doors share is the ceremony
// behind them, never the surface.
//
// THREE ORDERING FACTS, each a real defect if reversed:
//
//   1. Persistence is applied BEFORE the credential is sent — `session.ts`'s canonical ordering.
//      `setPersistence` is itself async, so a session established first can land at the default
//      scope before the switch takes effect (the webapp Dart footgun `session.ts` documents).
//   2. `finish` runs OUTSIDE the `catch`. A tail failure is a failure of the TAIL, and normalising
//      it through `withSecondFactorChallenge` would relabel e.g. the admin flow's company-lookup
//      refusal as a credential problem.
//   3. The tail is threaded THROUGH the challenge rather than re-run after it, so a challenged
//      sign-in lands on exactly the decision a clean one does (`mfa.ts`'s continuation contract).
//
// ⚠ THIS ESTABLISHES A SESSION AND IS DELIBERATELY UNBOUND. It accepts any account's credentials
// and swaps whoever was signed in — that is what signing in IS. Do not add a `User` parameter or an
// `auth.currentUser` check to make it "safer": a caller that needs identity BINDING wants
// `reauth.ts`, whose primitives reject a different account with `userMismatch` precisely because
// proving identity must not be able to swap the session. The two verbs are separate on purpose, and
// collapsing them is the exact failure AS-WEB3 removed from super-admin's money re-auth.

import { signInWithEmailAndPassword, type Auth, type UserCredential } from 'firebase/auth';
import { withSecondFactorChallenge } from './mfa';
import { applySessionPersistence } from './session';
import type { SessionPersistence } from './types';

/** The credential a door collects. One object, so email and password cannot swap at a call site. */
export interface EmailCredentials {
  email: string;
  password: string;
}

/** How a door parameterises the shared ceremony. Both fields are optional and independent. */
export interface EmailSignInOptions<T = UserCredential> {
  /**
   * The session scope to apply before the credential is sent. **Omit to leave the `Auth` instance's
   * persistence exactly as it is** — a real, distinct choice rather than a default: the
   * donate/consumer door has no "remember me" control to read, and a path that silently picked a
   * scope would be deciding session lifetime on the user's behalf. (The passkey path skips it for
   * the same reason — see `adminSignInWithPasskey`.) Doors that DO have the control pass
   * `persistenceForRememberMe(rememberMe)`, which keeps the mapping in one place and keeps the
   * chosen scope readable at the call site.
   */
  persistence?: SessionPersistence;
  /**
   * The flow's own tail, mapping the credential onto the door's success value — the admin flows'
   * company + email-verified routing decision, for instance. Omit when the success value IS the
   * credential, which is every door that only needs a session.
   *
   * It runs on BOTH paths and on neither more than once: directly on a clean sign-in, and as
   * `withSecondFactorChallenge`'s continuation once a challenged code is accepted. That is the
   * whole reason it is a parameter rather than something the caller does afterwards — a caller who
   * ran its tail after `await` would skip it entirely on the challenged path, which is how a
   * second-factor user ends up signed in but unrouted.
   */
  finish?: (credential: UserCredential) => Promise<T>;
}

/**
 * Sign in with email and password, applying the door's session scope, upgrading a pending TOTP
 * second factor into a {@link SecondFactorRequiredError} carrying the challenge, and running the
 * door's own tail on whichever path completes.
 *
 * Rejects with an {@link AuthError} — `invalidCredential` for a wrong email/password,
 * `tooManyRequests`, `networkRequestFailed`, and the rest of the mapped kinds; an enrolled account
 * rejects with `SecondFactorRequiredError`, which screens branch on via `secondFactorChallengeOf`.
 * The password is passed only to Firebase and is never logged or interpolated into an error.
 */
export async function signInWithEmail<T = UserCredential>(
  auth: Auth,
  credentials: EmailCredentials,
  options: EmailSignInOptions<T> = {},
): Promise<T> {
  // Identity when the door's success value is the credential; the cast is sound because `finish` is
  // omitted only when `T` was left at its `UserCredential` default. Same shape as `mfa.ts`.
  const complete =
    options.finish ?? ((credential: UserCredential) => Promise.resolve(credential as unknown as T));

  if (options.persistence !== undefined) {
    await applySessionPersistence(auth, options.persistence);
  }

  let credential: UserCredential;
  try {
    credential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
  } catch (error) {
    throw withSecondFactorChallenge(auth, error, complete);
  }
  return complete(credential);
}
